import React, { useContext, useEffect, useState } from 'react'
import Input from '@components/inputs/Input'
import { LinkButton } from '@components/Button'
import useRealm from '@hooks/useRealm'
import { getMintDecimalAmount, getMintMinAmountAsDecimal } from '@tools/sdk/units'
import { PublicKey } from '@solana/web3.js'
import { precision } from '@utils/formatting'
import {
  SplTokenTransferForm,
  UiInstruction,
} from '@utils/uiTypes/proposalCreationTypes'
import { getAccountName } from '@components/instructions/tools'
import { NewProposalContext } from '../../new'
import { getTokenTransferSchema } from '@utils/validations'
import useGovernanceAssets from '@hooks/useGovernanceAssets'
import { Governance } from '@solana/spl-governance'
import { ProgramAccount } from '@solana/spl-governance'
import GovernedAccountSelect from '../GovernedAccountSelect'
import {
  getSolTransferInstruction,
  getTransferInstruction,
} from '@utils/instructionTools'
import { useDestination } from '@hooks/useDestination'
import useWalletOnePointOh from '@hooks/useWalletOnePointOh'
import useLegacyConnectionContext from '@hooks/useLegacyConnectionContext'

const SplTokenTransfer = ({
  index,
  governance,
}: {
  index: number
  governance: ProgramAccount<Governance> | null
}) => {
  const connection = useLegacyConnectionContext()
  const wallet = useWalletOnePointOh()
  const { realmInfo } = useRealm()
  const { governedTokenAccountsWithoutNfts } = useGovernanceAssets()
  const shouldBeGoverned = !!(index !== 0 && governance)
  const programId: PublicKey | undefined = realmInfo?.programId
  const [form, setForm] = useState<SplTokenTransferForm>({
    destinationAccount: '',
    // No default transfer amount
    amount: undefined,
    governedTokenAccount: undefined,
    programId: programId?.toString(),
    mintInfo: undefined,
  })
  const [address, setAddress] = useState('')
  const [governedAccount, setGovernedAccount] = useState<
    ProgramAccount<Governance> | undefined
  >(undefined)
  const { destinationAccount, destinationAddress } = useDestination(
    connection.current,
    address,
  )
  const [formErrors, setFormErrors] = useState({})
  const mintMinAmount = form.mintInfo
    ? getMintMinAmountAsDecimal(form.mintInfo)
    : 1
  const currentPrecision = precision(mintMinAmount)
  const { handleSetInstructions } = useContext(NewProposalContext)

  const handleSetForm = ({ propertyName, value }) => {
    setFormErrors({})
    setForm({ ...form, [propertyName]: value })
  }
  const setMintInfo = (value) => {
    setForm({ ...form, mintInfo: value })
  }
  const setAmount = (event) => {
    const value = event.target.value
    handleSetForm({
      value: value,
      propertyName: 'amount',
    })
  }
  const setMaxAmount = () => {
    const mintAccount = form.governedTokenAccount?.extensions?.mint?.account
    const amount = form.governedTokenAccount?.extensions?.amount
    if (!mintAccount || !amount) return

    const maxAmount = getMintDecimalAmount(mintAccount, amount).toNumber()
    handleSetForm({
      value: maxAmount,
      propertyName: 'amount',
    })
  }
  const validateAmountOnBlur = () => {
    const value = form.amount

    handleSetForm({
      value: parseFloat(
        Math.max(
          Number(mintMinAmount),
          Math.min(Number(Number.MAX_SAFE_INTEGER), Number(value)),
        ).toFixed(currentPrecision),
      ),
      propertyName: 'amount',
    })
  }
  async function getInstruction(): Promise<UiInstruction> {
    return !form.governedTokenAccount?.isSol
      ? getTransferInstruction({
          schema,
          form,
          programId,
          connection,
          wallet,
          currentAccount: form.governedTokenAccount || null,
          setFormErrors,
        })
      : getSolTransferInstruction({
          schema,
          form,
          programId,
          connection,
          wallet,
          currentAccount: form.governedTokenAccount || null,
          setFormErrors,
        })
  }

  useEffect(() => {
    handleSetForm({
      propertyName: 'programId',
      value: programId?.toString(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO please fix, it can cause difficult bugs. You might wanna check out https://bobbyhadz.com/blog/react-hooks-exhaustive-deps for info. -@asktree
  }, [realmInfo?.programId])

  useEffect(() => {
    if (destinationAddress) {
      handleSetForm({
        value: destinationAddress.toBase58(),
        propertyName: 'destinationAccount',
      })
    } else {
      handleSetForm({
        value: '',
        propertyName: 'destinationAccount',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO please fix, it can cause difficult bugs. You might wanna check out https://bobbyhadz.com/blog/react-hooks-exhaustive-deps for info. -@asktree
  }, [destinationAddress])

  useEffect(() => {
    handleSetInstructions(
      { governedAccount: governedAccount, getInstruction },
      index,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO please fix, it can cause difficult bugs. You might wanna check out https://bobbyhadz.com/blog/react-hooks-exhaustive-deps for info. -@asktree
  }, [form])
  useEffect(() => {
    setGovernedAccount(form.governedTokenAccount?.governance)
    setMintInfo(form.governedTokenAccount?.extensions.mint?.account)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO please fix, it can cause difficult bugs. You might wanna check out https://bobbyhadz.com/blog/react-hooks-exhaustive-deps for info. -@asktree
  }, [form.governedTokenAccount])
  const destinationAccountName =
    destinationAccount?.publicKey &&
    getAccountName(destinationAccount?.account.address)
  const base58DestinationAddress = address.endsWith('.sol')
    ? form.destinationAccount
    : undefined
  const schema = getTokenTransferSchema({ form, connection })

  return (
    <>
      <GovernedAccountSelect
        label="Source account"
        governedAccounts={governedTokenAccountsWithoutNfts}
        onChange={(value) => {
          handleSetForm({ value, propertyName: 'governedTokenAccount' })
        }}
        value={form.governedTokenAccount}
        error={formErrors['governedTokenAccount']}
        shouldBeGoverned={shouldBeGoverned}
        governance={governance}
        type="token"
      ></GovernedAccountSelect>
      <Input
        label="Destination account"
        value={address}
        type="text"
        onChange={(evt) => setAddress(evt.target.value)}
        error={formErrors['destinationAccount']}
      />
      {base58DestinationAddress && (
        <div>
          <div className="pb-0.5 text-fgd-3 text-xs">{address}</div>
          <div className="text-xs">{base58DestinationAddress}</div>
        </div>
      )}
      {destinationAccount && (
        <div>
          <div className="pb-0.5 text-fgd-3 text-xs">Account owner</div>
          <div className="text-xs">
            {destinationAccount.account.owner.toString()}
          </div>
        </div>
      )}
      {destinationAccountName && (
        <div>
          <div className="pb-0.5 text-fgd-3 text-xs">Account name</div>
          <div className="text-xs">{destinationAccountName}</div>
        </div>
      )}
      <div>
        <div className="flex mb-1.5 text-sm">
          <span>Amount</span>
          <div className="ml-auto flex items-center text-xs">
            <LinkButton
              onClick={setMaxAmount}
              className="font-bold text-primary-light"
            >
              Max
            </LinkButton>
          </div>
        </div>
        <Input
          min={mintMinAmount}
          value={form.amount}
          type="number"
          onChange={setAmount}
          step={mintMinAmount}
          error={formErrors['amount']}
          onBlur={validateAmountOnBlur}
        />
      </div>
    </>
  )
}

export default SplTokenTransfer
